import React from 'react';
import BaseChart from './BaseChart';

/**
 * CostBreakdownChart Component
 *
 * Displays cost breakdown using a donut chart with:
 * - Cost distribution by model/provider
 * - Category breakdown on hover
 * - Interactive segments
 * - Cost percentages
 * - Responsive design
 */
const CostBreakdownChart = ({ data, period, loading = false }) => {
  if (loading || !data || !data.models || data.models.length === 0) {
    return (
      <BaseChart
        title={`Cost Breakdown (${period || 'N/A'})`}
        subtitle="Cost distribution by model/provider • Hover for category breakdown"
        type="donut"
        series={[]}
        height={400}
        loading={loading}
      />
    );
  }

  // Prepare chart data - limit to top 8 models for better readability
  const sortedModels = data.models
    .sort((a, b) => b.total_cost - a.total_cost)
    .slice(0, 8);

  const chartOptions = {
    labels: sortedModels.map(model => model.model_name),
    colors: [
      '#1976d2',
      '#4caf50',
      '#ff9800',
      '#f44336',
      '#9c27b0',
      '#00bcd4',
      '#ffeb3b',
      '#795548',
      '#607d8b',
    ],
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 600,
              color: '#333',
            },
            value: {
              show: true,
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#1976d2',
              formatter: function (val) {
                return '$' + parseFloat(val).toFixed(2);
              },
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total Cost',
              fontSize: '14px',
              fontWeight: 600,
              color: '#333',
              formatter: function (w) {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return '$' + total.toFixed(2);
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        return (
          opts.w.config.labels[opts.seriesIndex] + ': ' + val.toFixed(1) + '%'
        );
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const model = sortedModels[seriesIndex];
        const percentage = (
          (model.total_cost /
            sortedModels.reduce((sum, m) => sum + m.total_cost, 0)) *
          100
        ).toFixed(1);

        // Build category breakdown HTML
        const categoryBreakdown = model.category_breakdown
          .map(
            cat =>
              `<div style="margin-left: 12px; font-size: 12px; color: #666;">• ${cat.category}: $${cat.cost.toFixed(2)} (${cat.executions} execs)</div>`
          )
          .join('');

        return `
          <div style="padding: 10px; background: white; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 320px; width: max-content;">
            <div style="font-weight: bold; margin-bottom: 6px; color: #1976d2; font-size: 14px; word-wrap: break-word;">${model.model_name}</div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; margin-bottom: 8px; font-size: 12px;">
              <div>Total Cost: <strong>$${model.total_cost.toFixed(2)}</strong></div>
              <div>Percentage: <strong>${percentage}%</strong></div>
              <div>Executions: <strong>${model.execution_count.toLocaleString()}</strong></div>
              <div>Success Rate: <strong>${model.success_rate.toFixed(1)}%</strong></div>
            </div>
            <div style="margin-bottom: 4px; font-size: 12px;">Avg Cost: <strong>$${(model.total_cost / model.execution_count).toFixed(4)}</strong></div>
            <div style="font-weight: bold; margin-bottom: 4px; color: #333; font-size: 12px;">Category Breakdown:</div>
            ${categoryBreakdown}
          </div>
        `;
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 350,
          },
          plotOptions: {
            pie: {
              donut: {
                size: '60%',
                labels: {
                  name: {
                    fontSize: '12px',
                  },
                  value: {
                    fontSize: '14px',
                  },
                  total: {
                    fontSize: '12px',
                  },
                },
              },
            },
          },
        },
      },
    ],
  };

  const series = sortedModels.map(model => model.total_cost);

  return (
    <BaseChart
      title={`Cost Breakdown (${period || 'N/A'})`}
      subtitle="Cost distribution by model/provider • Hover for category breakdown"
      type="donut"
      series={series}
      options={chartOptions}
      height={400}
      loading={loading}
    />
  );
};

export default CostBreakdownChart;
